
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Clock, AlertTriangle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function PaymentResponse() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [paymentStatus, setPaymentStatus] = useState<string>('loading');

  useEffect(() => {
    const transactionState = searchParams.get('transactionState');
    const lapTransactionState = searchParams.get('lapTransactionState');
    const message = searchParams.get('message');
    const referenceCode = searchParams.get('referenceCode');
    const source = searchParams.get('source');

    console.log('Payment response page loaded with params:', {
      transactionState,
      lapTransactionState,
      message,
      referenceCode,
      source
    });

    if (transactionState === '4' || lapTransactionState === 'APPROVED') {
      setPaymentStatus('approved');
    } else if (transactionState === '6' || lapTransactionState === 'DECLINED') {
      setPaymentStatus('declined');
    } else if (transactionState === '7' || lapTransactionState === 'PENDING') {
      setPaymentStatus('pending');
    } else if (transactionState === '104' || lapTransactionState === 'ERROR') {
      setPaymentStatus('error');
    } else {
      setPaymentStatus('unknown');
    }
  }, [searchParams]);

  const getStatusConfig = () => {
    switch (paymentStatus) {
      case 'approved':
        return {
          icon: <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />,
          title: t('subscription.payu.approvedTitle'),
          message: t('subscription.payu.approvedMessage'),
          color: 'text-green-600',
          bgColor: 'bg-green-50 dark:bg-green-950/20',
          borderColor: 'border-green-200 dark:border-green-800'
        };
      case 'pending':
        return {
          icon: <Clock className="h-16 w-16 text-yellow-500 mx-auto mb-4" />,
          title: t('subscription.payu.pendingTitle'),
          message: t('subscription.payu.pendingMessage'),
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50 dark:bg-yellow-950/20',
          borderColor: 'border-yellow-200 dark:border-yellow-800'
        };
      case 'declined':
        return {
          icon: <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />,
          title: t('subscription.payu.declinedTitle'),
          message: t('subscription.payu.declinedMessage'),
          color: 'text-red-600',
          bgColor: 'bg-red-50 dark:bg-red-950/20',
          borderColor: 'border-red-200 dark:border-red-800'
        };
      case 'error':
        return {
          icon: <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />,
          title: t('subscription.payu.errorTitle'),
          message: t('subscription.payu.errorMessage'),
          color: 'text-red-600',
          bgColor: 'bg-red-50 dark:bg-red-950/20',
          borderColor: 'border-red-200 dark:border-red-800'
        };
      default:
        return {
          icon: <AlertTriangle className="h-16 w-16 text-muted-foreground mx-auto mb-4" />,
          title: t('paymentResponse.unknownTitle'),
          message: t('paymentResponse.unknownMessage'),
          color: 'text-muted-foreground',
          bgColor: 'bg-muted/50',
          borderColor: 'border-border'
        };
    }
  };

  const config = getStatusConfig();

  const handleRetryPayment = () => {
    navigate('/subscription');
    toast.info(t('paymentResponse.retryInfo'));
  };

  const handleGoToApp = () => {
    if (paymentStatus === 'approved') {
      navigate('/feed');
      toast.success(t('paymentResponse.welcomeActive'));
    } else {
      navigate('/subscription');
    }
  };

  const handleCheckEmail = () => {
    toast.info(t('paymentResponse.checkEmailInfo'));
    navigate('/subscription');
  };

  if (paymentStatus === 'loading') {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('paymentResponse.processing')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className={`${config.bgColor} ${config.borderColor} border rounded-lg p-6 text-center space-y-6`}>
          {config.icon}
          
          <div className="space-y-3">
            <h1 className={`text-2xl font-bold ${config.color}`}>
              {config.title}
            </h1>
            <p className="text-muted-foreground">
              {config.message}
            </p>
          </div>

          <div className="text-sm text-muted-foreground space-y-1">
            {searchParams.get('referenceCode') && (
              <p><strong>{t('paymentResponse.reference')}:</strong> {searchParams.get('referenceCode')}</p>
            )}
            {searchParams.get('message') && (
              <p><strong>{t('paymentResponse.message')}:</strong> {searchParams.get('message')}</p>
            )}
          </div>

          <div className="space-y-3">
            {paymentStatus === 'approved' && (
              <>
                <Button 
                  onClick={handleGoToApp}
                  className="w-full"
                >
                  {t('subscription.payu.continueButton')}
                </Button>
                <Button 
                  onClick={() => navigate('/subscription')}
                  variant="outline"
                  className="w-full"
                >
                  {t('paymentResponse.viewSubscription')}
                </Button>
              </>
            )}
            
            {paymentStatus === 'declined' && (
              <>
                <Button 
                  onClick={handleRetryPayment}
                  className="w-full"
                >
                  {t('subscription.payu.retryButton')}
                </Button>
                <Button 
                  onClick={handleGoToApp}
                  variant="outline"
                  className="w-full"
                >
                  {t('paymentResponse.returnToApp')}
                </Button>
              </>
            )}
            
            {(paymentStatus === 'pending' || paymentStatus === 'unknown' || paymentStatus === 'error') && (
              <>
                <Button 
                  onClick={handleCheckEmail}
                  className="w-full"
                >
                  {t('paymentResponse.checkEmail')}
                </Button>
                <Button 
                  onClick={handleGoToApp}
                  variant="outline"
                  className="w-full"
                >
                  {t('paymentResponse.returnToApp')}
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="mt-6 text-center">
          <Button 
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-muted-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('paymentResponse.goHome')}
          </Button>
        </div>
      </div>
    </div>
  );
}
